import { useState, useEffect } from 'react'
import { ChevronUp } from 'lucide-react'
import { toggleVote } from '../../lib/feedbackApi'

interface FeedbackVoteButtonProps {
  feedbackId: number
  initialHasVoted: boolean
  initialVoteCount: number
  onVoteChange?: (hasVoted: boolean, voteCount: number) => void
}

export function FeedbackVoteButton({
  feedbackId,
  initialHasVoted,
  initialVoteCount,
  onVoteChange,
}: FeedbackVoteButtonProps) {
  const [voting, setVoting] = useState(false)
  const [hasVoted, setHasVoted] = useState(initialHasVoted)
  const [voteCount, setVoteCount] = useState(initialVoteCount)

  // Sync with parent when props change (e.g. after refresh)
  useEffect(() => { setHasVoted(initialHasVoted) }, [initialHasVoted])
  useEffect(() => { setVoteCount(initialVoteCount) }, [initialVoteCount])

  async function handleVote(e: React.MouseEvent) {
    e.stopPropagation()
    if (voting) return
    setVoting(true)

    // Optimistic update
    const prevVoted = hasVoted
    const prevCount = voteCount
    setHasVoted(!prevVoted)
    setVoteCount(prevVoted ? prevCount - 1 : prevCount + 1)

    try {
      const result = await toggleVote(feedbackId)
      setHasVoted(result.has_voted)
      setVoteCount(result.vote_count)
      onVoteChange?.(result.has_voted, result.vote_count)
    } catch {
      // Revert on error
      setHasVoted(prevVoted)
      setVoteCount(prevCount)
    } finally {
      setVoting(false)
    }
  }

  return (
    <button
      onClick={handleVote}
      disabled={voting}
      className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg border transition-colors shrink-0 ${
        hasVoted
          ? 'bg-primary/10 border-primary/30 text-primary'
          : 'border-border text-muted-foreground hover:bg-secondary hover:text-foreground'
      }`}
    >
      <ChevronUp className="w-4 h-4" />
      <span className="text-xs font-semibold">{voteCount}</span>
    </button>
  )
}
